import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore, useUIStore, useToastStore, useTourStateStore } from '@/stores';
import { usePrefersReducedMotion } from '@/hooks/common/use-reduced-motion';
import { TourCard } from './TourCard';
import { TourOverlay } from './TourOverlay';
import { CURRENT_TOUR_VERSION, getTourSteps } from '../steps/tour-steps';
import { releaseNotesSince } from '../steps/release-notes';
import { tourSelector, waitForElement } from '../utils/wait-for-element';
import { isTourSyntheticEvent } from '../utils/synthetic-key-event';
import {
  clearDismissed,
  clearReminderCount,
  getReminderCount,
  incrementReminderCount,
} from '../utils/dismiss-flag';
import {
  clearProgress,
  writeProgress,
} from '../utils/tour-progress';
import {
  REMIND_LATER_LIMIT,
  useAutoStart,
  useResumeProgress,
} from '../hooks/tour-lifecycle';
import { persistOnboardingSeenVersion } from '../hooks/tour-persistence';
import {
  getFrameCount,
  getVisibleSteps,
  resolveFrame,
  resolveMedia,
  type ResolvedFrame,
  type TourFrame,
  type TourMedia,
  type TourStep,
} from '../types';

// Re-export so existing public consumers (`import { REMIND_LATER_LIMIT }
// from '@/features/onboarding/components/TourProvider'`) keep working
// after the constant moved to the lifecycle hook module.
export { REMIND_LATER_LIMIT };

// Bounding union of two rects, exposed as a DOMRect-shaped object so the
// spotlight cutout can encompass both the primary target and an optional
// secondary popover that lives in a different DOM subtree.
//
// Guards against detached secondaries: when a frame transitions away from a
// step that opened a popover (e.g. Statement → next frame), the secondary
// element is removed from the DOM but the ResizeObserver here can still fire
// once with the old refs before the apply effect clears them. A detached
// element reports `(0, 0, 0, 0)`, the union slumps to the viewport origin,
// and the spotlight cutout briefly flickers to the wrong place.
const isUsableRect = (el: HTMLElement | null): boolean => {
  if (!el || !el.isConnected) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0;
};

const unionRect = (
  primary: HTMLElement | null,
  secondary: HTMLElement | null,
): DOMRect | null => {
  const usePrimary = isUsableRect(primary);
  const useSecondary = isUsableRect(secondary);
  if (!usePrimary && !useSecondary) return null;
  if (!usePrimary) return secondary!.getBoundingClientRect();
  if (!useSecondary) return primary!.getBoundingClientRect();
  const a = primary!.getBoundingClientRect();
  const b = secondary!.getBoundingClientRect();
  const left = Math.min(a.left, b.left);
  const top = Math.min(a.top, b.top);
  const right = Math.max(a.right, b.right);
  const bottom = Math.max(a.bottom, b.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
};

export type TourMode = 'full' | 'whats-new';

export interface StartOptions {
  /** 'full' replays the entire tour from step 0; 'whats-new' filters to
   * steps where addedInVersion > user.onboardingSeenVersion so users
   * upgrading across releases only see the new content. Defaults to
   * 'full' when omitted (backwards-compatible with sidebar callers). */
  mode?: TourMode;
}

interface TourApi {
  isActive: boolean;
  /** Current tour mode. 'full' until a whats-new start. */
  mode: TourMode;
  start: (opts?: StartOptions) => void;
  next: () => void;
  back: () => void;
  skip: () => void;
  /**
   * Jump directly to a frame within the current step. Awaits the current
   * frame's onLeave (if any), then sets the new frame index — the apply
   * effect picks up onEnter automatically. No-op when the current step
   * has only one frame, or when the target index equals the current.
   */
  goToFrame: (targetIndex: number) => void;
  /**
   * Stop the tour WITHOUT persisting seen_version. Increments a per-version
   * reminder counter in localStorage. The TourCard hides the "Remind me
   * later" button once the counter reaches REMIND_LATER_LIMIT.
   */
  remindLater: () => void;
  /** Current reminder count for CURRENT_TOUR_VERSION. Drives the visibility
   * of the "Remind me later" button on TourCard. */
  reminderCount: number;
}

const TourContext = createContext<TourApi | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- hook paired by design with TourProvider in this file
export const useTour = (): TourApi => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
};

const MIN_VIEWPORT_WIDTH = 1024;

/**
 * Build the same step list that the visibleSteps memo and start() compute.
 * Centralised so the three call sites (memo, empty-filter early return in
 * whats-new mode, pre-navigate route lookup in start()) cannot drift in
 * filter behaviour. `navigate` is forwarded into `getTourSteps` because
 * step-level closures (`buildNavigateToReportTab`, etc.) close over it.
 */
const buildFilteredSteps = (
  user: ReturnType<typeof useAuthStore.getState>['user'],
  navigate: ReturnType<typeof useNavigate>,
  mode: TourMode,
): TourStep[] => {
  const base = getVisibleSteps(
    getTourSteps(user, navigate),
    user?.permissions ?? [],
  );
  if (mode !== 'whats-new') return base;
  // What's-new content is sourced from the decoupled release-notes registry,
  // NOT the tour steps. Each note either deep-links into an existing (and
  // permission-visible) tour step via `tourStepId`, or — for a release-only
  // note — renders as a synthesized centered card. This lets a feature be
  // announced to returning users without adding a global-tour step (and a tour
  // step exist without being announced).
  const seen = user?.onboardingSeenVersion ?? 0;
  const byId = new Map(base.map((s) => [s.id, s] as const));
  return releaseNotesSince(seen).map((note) => {
    const linked = note.tourStepId ? byId.get(note.tourStepId) : undefined;
    if (linked) return linked;
    return {
      id: `rn-${note.id}`,
      title: note.title,
      body: note.summary,
      media: note.media,
      addedInVersion: note.version,
    } satisfies TourStep;
  });
};

interface ProviderProps {
  children: React.ReactNode;
}

export const TourProvider: React.FC<ProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const addToast = useToastStore((s) => s.addToast);
  const setSidebarCollapsed = useUIStore((s) => s.setSidebarCollapsed);
  // Mirror `isActive` into a top-level Zustand store so consumers (modals
  // that need to suppress click-outside) can subscribe without depending on
  // the TourProvider context. See `useTourStateStore` docstring.
  const setTourActive = useTourStateStore((s) => s.setActive);
  const prefersReducedMotion = usePrefersReducedMotion();

  const [isActive, setIsActive] = useState(false);
  const [tourMode, setTourMode] = useState<TourMode>('full');
  const [stepIndex, setStepIndex] = useState(0);
  const [frameIndex, setFrameIndex] = useState(0);
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  const [secondaryEl, setSecondaryEl] = useState<HTMLElement | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [tooSmallViewport, setTooSmallViewport] = useState(false);
  // Reminder counter is read from localStorage on mount and bumped via
  // remindLater(). Re-rendering the provider on bump lets TourCard hide
  // the affordance once the limit is reached.
  const [reminderCount, setReminderCount] = useState<number>(() =>
    getReminderCount(CURRENT_TOUR_VERSION),
  );

  const triggeredThisSessionRef = useRef(false);
  // Capture the user's pre-existing sidebarCollapsed preference at start()
  // and restore it at stop(). The tour always wants the sidebar open (the
  // first step targets it), but a user who keeps the sidebar collapsed by
  // default should not have that preference clobbered after the tour ends.
  // null sentinel = "not currently captured" — we intentionally do NOT
  // overwrite an existing capture if start() is called twice in a row, so
  // an interrupted tour still restores the original value.
  const sidebarCollapsedAtStartRef = useRef<boolean | null>(null);
  // Track the most recently-entered step so we can fire its onLeave on
  // step transitions and on stop. Refs (not state) — onLeave is a side
  // effect; we don't want to re-render or re-run the apply effect when
  // the bookkeeping changes.
  const previousStepRef = useRef<TourStep | null>(null);
  // Track the most recently-applied frame so we can fire its onLeave on
  // every frame transition (Next/Back/goToFrame). Refs (not state) — this
  // is bookkeeping for a side effect, not user-visible state.
  const previousFrameRef = useRef<TourFrame | null>(null);

  const runOnLeave = useCallback((step: TourStep | null) => {
    if (!step?.onLeave) return;
    try {
      const result = step.onLeave();
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).catch((err) => {

          console.warn(`[onboarding] onLeave for step "${step.id}" failed:`, err);
        });
      }
    } catch (err) {

      console.warn(`[onboarding] onLeave for step "${step.id}" failed:`, err);
    }
  }, []);

  /** Mirror of runOnLeave for frame-level teardown. Frames don't carry an
   * id so log the previous frame's title (or "<untitled>") for debugging. */
  const runFrameOnLeave = useCallback((frame: TourFrame | null) => {
    if (!frame?.onLeave) return;
    const label = frame.title ?? '<untitled>';
    try {
      const result = frame.onLeave();
      if (result && typeof (result as Promise<void>).then === 'function') {
        (result as Promise<void>).catch((err) => {

          console.warn(`[onboarding] frame onLeave for "${label}" failed:`, err);
        });
      }
    } catch (err) {

      console.warn(`[onboarding] frame onLeave for "${label}" failed:`, err);
    }
  }, []);

  const visibleSteps: TourStep[] = useMemo(() => {
    const filtered = buildFilteredSteps(user, navigate, tourMode);
    // Dev-time guard against duplicate step ids. The apply / onLeave
    // bookkeeping uses `previousStepRef.current.id !== currentStep.id` to
    // detect step boundaries; if two steps shared an id, onLeave for one
    // of them would never fire. Cheap O(N) scan, no production penalty
    // worth measuring. Run on the unfiltered base so a step that's hidden
    // by `whats-new` filter still gets its id checked.
    if (import.meta.env.DEV) {
      // `filtered` already IS the full set in 'full' mode — only rebuild for
      // whats-new mode (a subset) so the duplicate-id check still sees every
      // step without constructing the step list twice on every full-mode render.
      const fullSet =
        tourMode === 'full' ? filtered : buildFilteredSteps(user, navigate, 'full');
      const ids = fullSet.map((s) => s.id);
      if (new Set(ids).size !== ids.length) {

        console.error(
          '[onboarding] duplicate step ids detected in tour-steps:',
          ids,
        );
      }
    }
    return filtered;
  }, [user, tourMode, navigate]);

  const currentStep = visibleSteps[stepIndex] ?? null;
  const frameCount = currentStep ? getFrameCount(currentStep) : 1;
  const safeFrameIndex = Math.min(frameIndex, frameCount - 1);
  // Memoize so the effect below does not re-run on every render.
  const currentFrame: ResolvedFrame | null = useMemo(
    () => (currentStep ? resolveFrame(currentStep, safeFrameIndex) : null),
    [currentStep, safeFrameIndex],
  );

  /**
   * Resolve the media that the *next* tour position will render. Used by
   * TourCard → TourMedia for lazy <link rel="preload" as="video"> on
   * upcoming video assets. We look ahead by one frame within the current
   * step first, then fall through to the next visible step's first frame.
   */
  const nextMedia: TourMedia | undefined = useMemo(() => {
    if (!currentStep) return undefined;
    const fc = getFrameCount(currentStep);
    if (safeFrameIndex < fc - 1) {
      const nextFrame = currentStep.frames?.[safeFrameIndex + 1] ?? null;
      return resolveMedia(currentStep, nextFrame);
    }
    const nextStep = visibleSteps[stepIndex + 1];
    if (!nextStep) return undefined;
    const nextFirstFrame = nextStep.frames?.[0] ?? null;
    return resolveMedia(nextStep, nextFirstFrame);
  }, [currentStep, safeFrameIndex, visibleSteps, stepIndex]);

  const stop = useCallback(() => {
    // Fire onLeave for the last active frame, then the last active step.
    // Frame teardown runs first so a frame's modal/tab cleanup happens
    // before the step's broader cleanup.
    runFrameOnLeave(previousFrameRef.current);
    previousFrameRef.current = null;
    runOnLeave(previousStepRef.current);
    previousStepRef.current = null;
    // Restore the user's pre-tour sidebarCollapsed preference, if captured.
    if (sidebarCollapsedAtStartRef.current !== null) {
      setSidebarCollapsed(sidebarCollapsedAtStartRef.current);
      sidebarCollapsedAtStartRef.current = null;
    }
    // Wipe the per-tab progress: a stop() means the user is intentionally
    // out of the tour, so a refresh after this point should NOT resume.
    clearProgress();
    setIsActive(false);
    setTargetEl(null);
    setSecondaryEl(null);
    setTargetRect(null);
    setStepIndex(0);
    setFrameIndex(0);
    setTourMode('full');
  }, [runOnLeave, runFrameOnLeave, setSidebarCollapsed]);

  const start = useCallback(
    async (opts?: StartOptions) => {
      if (window.innerWidth < MIN_VIEWPORT_WIDTH) {
        setTooSmallViewport(true);
        return;
      }
      const mode: TourMode = opts?.mode ?? 'full';
      const filteredSteps = buildFilteredSteps(user, navigate, mode);
      // Whats-new with no new steps: persist seen_version and return without
      // rendering anything. Otherwise visibleSteps would be empty, currentStep
      // would be null, and the user would sit on isActive=true with no card.
      // The banner already gates on `seenVersion < CURRENT_TOUR_VERSION` so
      // this mostly catches a future bug where addedInVersion bumps lag the
      // CURRENT_TOUR_VERSION constant.
      if (mode === 'whats-new' && filteredSteps.length === 0) {
        void persistOnboardingSeenVersion(user, setUser, addToast);
        return;
      }
      triggeredThisSessionRef.current = true;
      // Capture the user's pre-tour sidebar preference exactly once per
      // start→stop cycle. If the ref is already populated (e.g. start()
      // called a second time before stop()), we keep the original capture
      // — that value is still what the user prefers post-tour.
      if (sidebarCollapsedAtStartRef.current === null) {
        sidebarCollapsedAtStartRef.current = useUIStore.getState().sidebarCollapsed;
      }
      setTourMode(mode);
      setStepIndex(0);
      setFrameIndex(0);
      setIsActive(true);
      // Pre-navigate to the first visible step's route. Full-mode this is
      // welcome (`/inventory`); whats-new mode could land elsewhere when
      // the first new-since-seen step lives on `/trades` etc. Falling back
      // to `/inventory` covers the case where step 0 has no explicit route.
      // The apply effect re-navigates to the same route on step boundary,
      // which is a no-op when we already landed here.
      const firstRoute: string = filteredSteps[0]?.route ?? '/inventory';
      await navigate({ to: firstRoute });
    },
    [navigate, user, setUser, addToast],
  );

  const skip = useCallback(() => {
    stop();
    void persistOnboardingSeenVersion(user, setUser, addToast);
  }, [stop, user, setUser, addToast]);

  const remindLater = useCallback(() => {
    // Bump the counter first so the auto-start guard sees the new value
    // when this user reloads. stop() does NOT call persistOnboardingSeenVersion,
    // so the user is intentionally left at their old seen_version on the
    // server — the server-side state stays "tour pending" but the client
    // remembers the rate-limit.
    const next = incrementReminderCount(CURRENT_TOUR_VERSION);
    setReminderCount(next);
    stop();
  }, [stop]);

  const done = useCallback(() => {
    stop();
    void persistOnboardingSeenVersion(user, setUser, addToast);
  }, [stop, user, setUser, addToast]);

  const next = useCallback(() => {
    if (!currentStep) return;
    const fc = getFrameCount(currentStep);
    if (safeFrameIndex < fc - 1) {
      setFrameIndex((i) => i + 1);
      return;
    }
    if (stepIndex >= visibleSteps.length - 1) {
      done();
      return;
    }
    setStepIndex((i) => i + 1);
    setFrameIndex(0);
  }, [currentStep, safeFrameIndex, stepIndex, visibleSteps.length, done]);

  const back = useCallback(() => {
    if (safeFrameIndex > 0) {
      setFrameIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (stepIndex === 0) return;
    const prevStep = visibleSteps[stepIndex - 1];
    setStepIndex((i) => Math.max(0, i - 1));
    setFrameIndex(prevStep ? getFrameCount(prevStep) - 1 : 0);
  }, [safeFrameIndex, stepIndex, visibleSteps]);

  /**
   * Jump to a specific frame within the current step. No-op when:
   *   - there is no current step,
   *   - the step has only one frame (or none),
   *   - the target index equals the current safe frame index,
   *   - the target index is out of bounds.
   *
   * The previous frame's onLeave fires automatically via the frame-onLeave
   * effect (deps `[stepIndex, frameIndex]`); the new frame's onEnter
   * fires via the apply effect. We do not run them inline here so the
   * lifecycle ordering matches Next/Back exactly.
   */
  const goToFrame = useCallback(
    (targetIndex: number) => {
      if (!currentStep) return;
      const fc = getFrameCount(currentStep);
      if (fc <= 1) return;
      if (targetIndex < 0 || targetIndex >= fc) return;
      if (targetIndex === safeFrameIndex) return;
      setFrameIndex(targetIndex);
    },
    [currentStep, safeFrameIndex],
  );

  // Frame-level onLeave runner. Fires whenever the (stepIndex, frameIndex)
  // pair changes — that covers Next, Back, goToFrame, AND the implicit
  // frame change at a step boundary (frameIndex resets to 0 on new step).
  // Declared ABOVE the apply effect so when both run for the same state
  // change, the previous frame tears down before the new one's onEnter
  // runs. Effect ordering in React is by source order, so this matters.
  useEffect(() => {
    if (!isActive) return;
    const prev = previousFrameRef.current;
    if (prev) {
      runFrameOnLeave(prev);
    }
    // Update the bookkeeping ref to the *raw* frame for the new position.
    // Use the raw TourFrame (not ResolvedFrame) because onLeave lives on
    // the raw type. When the step has no frames array, there's nothing to
    // track at the frame level — clear the ref.
    previousFrameRef.current = currentStep?.frames?.[safeFrameIndex] ?? null;
    // No cleanup function: the next state change re-runs this effect and
    // does the teardown via the runFrameOnLeave call above. Using a
    // cleanup would fire on unmount only after the *next* render, which
    // is the wrong order for our use case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, stepIndex, safeFrameIndex]);

  // Apply the current step+frame: navigate (step-level route), run frame.onEnter, wait for target.
  useEffect(() => {
    if (!isActive || !currentStep || !currentFrame) return;
    let cancelled = false;
    const resampleTimers: ReturnType<typeof setTimeout>[] = [];

    const apply = async () => {
      // Step boundary: entering a different step than the one we last applied
      // (forward OR backward). Computed before we overwrite previousStepRef.
      const isStepBoundary =
        !previousStepRef.current ||
        previousStepRef.current.id !== currentStep.id;
      // Fire previous step's onLeave before entering the new one. Frame
      // transitions inside the same step do not trigger onLeave.
      if (
        previousStepRef.current &&
        previousStepRef.current.id !== currentStep.id
      ) {
        runOnLeave(previousStepRef.current);
      }
      previousStepRef.current = currentStep;

      // Route is step-level: navigate when we cross a STEP BOUNDARY, not on
      // every frame. The old guard (`safeFrameIndex === 0`) was a faulty proxy
      // for "boundary" — stepping BACK into a multi-frame step lands on its
      // LAST frame (index N-1, not 0), so the navigate was skipped and the
      // page (and spotlight) stayed on the step we came from. Keying off the
      // boundary itself re-establishes the route's page on back-entry too,
      // while still not re-firing on intra-step frame changes. (Steps that
      // navigate via onEnter — the trade surfaces — carry no `route` and are
      // unaffected.)
      if (currentStep.route && isStepBoundary) {
        const target: string = currentStep.route;
        await navigate({ to: target });
      }
      if (currentFrame.onEnter) {
        await currentFrame.onEnter();
      }
      if (cancelled) return;

      // Hold the old spotlight on screen while we resolve the new target.
      // Clearing eagerly at the top of `apply` produced a visible "dim
      // flash + centered card" between every step — the previous spotlight
      // would disappear, the card would jump to viewport center, then
      // re-anchor to the new target a few frames later. Instead, we leave
      // the old refs in place; the only branch that explicitly clears is
      // the no-target case below, where there is no new anchor to hold.
      if (currentFrame.targetSelector) {
        const el = await waitForElement(
          tourSelector(currentFrame.targetSelector),
          2000,
        );
        if (cancelled) return;
        if (el) {
          let secondary: HTMLElement | null = null;
          if (currentFrame.secondarySelector) {
            // The popover is already visible (onEnter opened it); the
            // wait covers React commit + paint, not a network round trip.
            secondary = await waitForElement(
              tourSelector(currentFrame.secondarySelector),
              1000,
            );
            if (cancelled) return;
          }
          // Honor prefers-reduced-motion: 'auto' is an instant jump,
          // 'smooth' animates. Some users get motion-sick from auto-scroll.
          //
          // `disableScrollIntoView` opts a frame out entirely. Used by frames
          // that scroll their own container in `onEnter` (e.g. trade-form
          // sections that position themselves to a specific ratio of the
          // modal-internal form). Without the opt-out, this re-center call
          // fights the manual scroll and the card ends up overlapping its
          // spotlit area.
          if (!currentFrame.disableScrollIntoView) {
            el.scrollIntoView({
              block: 'center',
              behavior: prefersReducedMotion ? 'auto' : 'smooth',
            });
          }
          // Atomic swap. React 18 batches these three setters in the same
          // tick, so the spotlight cuts straight from the old anchor to
          // the new one with no in-between blank frame.
          setTargetEl(el);
          setSecondaryEl(secondary);
          setTargetRect(unionRect(el, secondary));
          // Resample to absorb late layout settles the ResizeObserver
          // doesn't catch:
          //   1. A portaled popover (Statement calendar) reports its
          //      pre-positioned rect at (0, 0) until its position effect
          //      runs in the next frame.
          //   2. The filter sidebar plays a 300ms width transition when
          //      onEnter expands it from collapsed; the rect we read
          //      here can be mid-animation.
          // 16/100/350ms covers the next-frame fix and a CSS transition's
          // full duration. Handles are tracked so the cleanup below can
          // clear them — the `cancelled` flag would already short-circuit
          // the state update, but pending timers still pollute the event
          // loop on rapid step transitions.
          for (const delay of [16, 100, 350]) {
            const handle = setTimeout(() => {
              if (cancelled) return;
              setTargetRect(unionRect(el, secondary));
            }, delay);
            resampleTimers.push(handle);
          }
        } else {

          console.warn(`[onboarding] target not found: ${currentFrame.targetSelector}`);
          setTargetEl(null);
          setSecondaryEl(null);
          setTargetRect(null);
        }
      } else {
        // Welcome / Counterparties registry / final share this branch:
        // no anchor for this frame, so explicitly drop the previous
        // spotlight. Centering the card is the intended layout here.
        setTargetEl(null);
        setSecondaryEl(null);
        setTargetRect(null);
      }
    };

    void apply();
    return () => {
      cancelled = true;
      // Clear pending resample timers — `cancelled` already prevents the
      // state update inside each callback, but unfired timers still sit
      // in the event loop. On rapid Next/Back cycles this can stack up
      // dozens of no-op callbacks per second.
      resampleTimers.forEach((h) => clearTimeout(h));
    };
  }, [
    isActive,
    currentFrame,
    safeFrameIndex,
    currentStep,
    navigate,
    runOnLeave,
    prefersReducedMotion,
  ]);

  // Persist tour progress on every step/frame change so a mid-tour refresh
  // resumes at the same position. Cleared by stop() / on completion.
  useEffect(() => {
    if (!isActive) return;
    writeProgress({
      stepIndex,
      frameIndex: safeFrameIndex,
      version: CURRENT_TOUR_VERSION,
    });
  }, [isActive, stepIndex, safeFrameIndex]);

  // Mirror provider's `isActive` into the dedicated Zustand store so
  // non-onboarding consumers (modals, ambient UI) can read it without
  // pulling in the full TourProvider context. Cleanup resets to false on
  // unmount so a stale `true` can't leak across remounts.
  useEffect(() => {
    setTourActive(isActive);
    return () => {
      setTourActive(false);
    };
  }, [isActive, setTourActive]);

  // Track target rect on resize/scroll for spotlight cutout. When a
  // secondary element is attached (frame.secondarySelector), the spotlight
  // rect becomes the bounding union of both — used for popovers (e.g.
  // calendar dropdown) that render outside the primary target's subtree.
  useEffect(() => {
    if (!isActive || !targetEl) return;
    const update = () => setTargetRect(unionRect(targetEl, secondaryEl));
    const ro = new ResizeObserver(update);
    ro.observe(targetEl);
    if (secondaryEl) ro.observe(secondaryEl);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isActive, targetEl, secondaryEl]);

  // Keyboard navigation: Esc skips, ←/→/Enter advance/rewind. The card
  // already traps Tab so these shortcuts are layered on top without
  // conflicting. Enter inside an input/textarea/contentEditable is
  // *not* hijacked — typing in a form during the tour must work.
  useEffect(() => {
    if (!isActive) return;
    const isTypingTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      if (target instanceof HTMLInputElement) return true;
      if (target instanceof HTMLTextAreaElement) return true;
      if (target.isContentEditable) return true;
      return false;
    };
    const handler = (e: KeyboardEvent) => {
      // Ignore keyboard events the tour dispatched itself (e.g. the Trades
      // "Row actions" frame's onLeave simulates Escape to clear row selection
      // for useRowSelection). That synthetic Escape bubbles up to this
      // window-level listener; without this guard it would skip() the whole
      // tour (LR-446).
      if (isTourSyntheticEvent(e)) return;
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          skip();
          return;
        case 'ArrowLeft':
          e.preventDefault();
          back();
          return;
        case 'ArrowRight':
          e.preventDefault();
          next();
          return;
        case 'Enter':
          if (isTypingTarget(e.target)) return;
          e.preventDefault();
          next();
          return;
        default:
          return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, skip, next, back]);

  // Reset per-session and per-version state when a different user logs in
  // within the same tab (shared workstation). Without this, user A taking
  // the tour suppresses user B's auto-start when they log in next: the
  // session ref hides B from auto-start, and the localStorage flags
  // (dismissed + reminder_count) are per-version, not per-user, so B
  // inherits A's "already dismissed" / "remind-limit reached" state and
  // the silent path PATCHes B straight to seen=CURRENT_TOUR_VERSION.
  // The very-first render also flows through here — that's harmless because
  // the ref starts at `false` already and clearing absent keys is a no-op.
  const prevEmailRef = useRef<string | null>(null);
  useEffect(() => {
    const currentEmail = user?.email ?? null;
    if (
      prevEmailRef.current !== null &&
      prevEmailRef.current !== currentEmail
    ) {
      triggeredThisSessionRef.current = false;
      clearDismissed(CURRENT_TOUR_VERSION);
      clearReminderCount(CURRENT_TOUR_VERSION);
    }
    prevEmailRef.current = currentEmail;
  }, [user?.email]);

  // Resume-on-mount + auto-start are extracted into dedicated hooks for
  // readability — the original effects had >40 lines each and shared
  // refs / setters with the rest of the provider. See `tour-lifecycle.ts`.
  useResumeProgress({
    user,
    isActive,
    visibleStepsLength: visibleSteps.length,
    setStepIndex,
    setFrameIndex,
    setIsActive,
    sidebarCollapsedAtStartRef,
    triggeredThisSessionRef,
  });

  useAutoStart({
    user,
    isActive,
    start,
    setUser,
    triggeredThisSessionRef,
  });

  const api: TourApi = useMemo(
    () => ({
      isActive,
      mode: tourMode,
      start: (opts?: StartOptions) => void start(opts),
      next,
      back,
      skip,
      goToFrame,
      remindLater,
      reminderCount,
    }),
    [isActive, tourMode, start, next, back, skip, goToFrame, remindLater, reminderCount],
  );

  return (
    <TourContext.Provider value={api}>
      {children}
      {isActive && currentStep && currentFrame &&
        // Portal the overlay + card to <body> so they sit OUTSIDE the React
        // app root. Headless-UI Dialog applies `inert` + `aria-hidden` to
        // every body-level sibling of its own portal when it opens — if the
        // tour card stayed inside #root, the entire React tree (including
        // the card) would be marked inert and clicks on Next/Back would
        // silently fail. Portaling guarantees the tour card lives in its
        // own body sibling, so Dialog's inert dance can't touch it.
        createPortal(
          <>
            <TourOverlay rect={targetRect} />
            <TourCard
              step={currentStep}
              frame={currentFrame}
              stepNumber={stepIndex + 1}
              totalSteps={visibleSteps.length}
              frameNumber={safeFrameIndex + 1}
              frameCount={frameCount}
              targetEl={targetEl}
              secondaryEl={secondaryEl}
              isFirst={stepIndex === 0 && safeFrameIndex === 0}
              isLast={
                stepIndex === visibleSteps.length - 1 &&
                safeFrameIndex === frameCount - 1
              }
              onNext={next}
              onBack={back}
              onSkip={skip}
              // "Remind me later" is only meaningful for the whats-new
              // upgrade tour. First-run users (mode === 'full') should not
              // see the affordance — Skip + Done are the only exits. The
              // reminder counter / limit logic in this provider is unchanged;
              // only the button's visibility is mode-gated.
              onRemindLater={tourMode === 'whats-new' ? remindLater : undefined}
              hideRemindLater={reminderCount >= REMIND_LATER_LIMIT}
              nextMedia={nextMedia}
              onGoToFrame={goToFrame}
            />
          </>,
          document.body,
        )
      }
      {tooSmallViewport && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[10002] flex items-center justify-center bg-slate-900/60 p-4"
        >
          <div className="max-w-sm rounded-lg bg-white p-6 shadow-2xl">
            <h2 className="text-base font-semibold text-gray-900">
              Open LendRail on a larger screen
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              The product tour is best viewed on desktop (1024px or wider).
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setTooSmallViewport(false);
                  void persistOnboardingSeenVersion(user, setUser, addToast);
                }}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
};
